import Feature from 'ol/Feature';
import { Point } from 'ol/geom';
import type OlGeometry from 'ol/geom/Geometry';
import { Fill, RegularShape, Style } from 'ol/style';
import type { StyleFunction } from 'ol/style/Style';
import type VectorSource from 'ol/source/Vector';

/**
 * Desired screen-pixel spacing between arrows.
 * At any zoom level, arrows will be ~this many pixels apart.
 */
const ARROW_SCREEN_SPACING_PX = 100;

/**
 * Resolution-aware style function for arrow features.
 * Shows fewer arrows when zoomed out by skipping indices
 * that would be too close together in screen space.
 */
const arrowStyleFunction: StyleFunction = (featureLike, resolution) => {
  const feature = featureLike as Feature<OlGeometry>;
  const step = feature.get('_arrowStep') as number;
  const index = feature.get('_arrowIndex') as number;

  // How many map units per pixel at this zoom
  // Skip arrows that would be closer than ARROW_SCREEN_SPACING_PX pixels apart
  const mapUnitsPerArrow = ARROW_SCREEN_SPACING_PX * resolution;
  const skipFactor = Math.max(1, Math.ceil(mapUnitsPerArrow / step));

  // Only show every Nth arrow
  if (index % skipFactor !== 0) return;

  const rotation = feature.get('_arrowRotation') as number;
  const color = feature.get('_arrowColor') as string;

  return new Style({
    image: new RegularShape({
      points: 3,
      radius: 7,
      rotation,
      fill: new Fill({ color }),
    }),
  });
};

/**
 * Triangle arrow markers along a line (coordinates in the map projection).
 * Arrows are resolution-aware, so fewer show when zoomed out and more when
 * zoomed in.
 */
export function createDirectionArrowFeatures(
  coords: number[][],
  color: string,
): Feature<OlGeometry>[] {
  if (coords.length < 2) return [];

  // Compute cumulative distances (in map units)
  const cumDist: number[] = [0];
  for (let i = 1; i < coords.length; i++) {
    const dx = coords[i][0] - coords[i - 1][0];
    const dy = coords[i][1] - coords[i - 1][1];
    cumDist.push(cumDist[i - 1] + Math.sqrt(dx * dx + dy * dy));
  }

  const totalDist = cumDist[cumDist.length - 1];
  if (totalDist === 0) return [];

  // Place arrows densely (every ~1% of track or 50 max), then filter at render
  const maxArrows = Math.min(50, Math.max(5, Math.floor(totalDist / 200)));
  const step = totalDist / (maxArrows + 1);
  const features: Feature<OlGeometry>[] = [];

  for (let a = 1; a <= maxArrows; a++) {
    const targetDist = a * step;

    // Find the segment containing this distance
    let segIdx = 0;
    for (let i = 1; i < cumDist.length; i++) {
      if (cumDist[i] >= targetDist) {
        segIdx = i - 1;
        break;
      }
    }

    const segStart = coords[segIdx];
    const segEnd = coords[segIdx + 1];
    if (!segStart || !segEnd) continue;

    // Interpolate position along segment
    const segLen = cumDist[segIdx + 1] - cumDist[segIdx];
    const t = segLen > 0 ? (targetDist - cumDist[segIdx]) / segLen : 0;
    const x = segStart[0] + t * (segEnd[0] - segStart[0]);
    const y = segStart[1] + t * (segEnd[1] - segStart[1]);

    // Compute angle of travel
    const dx = segEnd[0] - segStart[0];
    const dy = segEnd[1] - segStart[1];
    const rotation = -Math.atan2(dy, dx) + Math.PI / 2;

    const arrowFeature = new Feature({ geometry: new Point([x, y]) });
    // Store metadata for resolution-aware visibility
    arrowFeature.set('_arrowIndex', a);
    arrowFeature.set('_arrowStep', step);
    arrowFeature.set('_arrowRotation', rotation);
    arrowFeature.set('_arrowColor', color);

    arrowFeature.setStyle(arrowStyleFunction);
    features.push(arrowFeature);
  }

  return features;
}

/** Add direction arrows along `coords` to a vector source. */
export function addDirectionArrows(
  source: VectorSource<Feature<OlGeometry>>,
  coords: number[][],
  color: string,
): void {
  source.addFeatures(createDirectionArrowFeatures(coords, color));
}
